import React, { useCallback, useEffect } from "react";
import { connect } from "react-redux";

import { faAnglesRight } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Drawer from "antd/es/drawer";
import cn from "classnames";
import { useIntl } from "react-intl";

import { EntityType } from "@mapmycustomers/shared";
import { EntityTypeSupportingDataView } from "@mapmycustomers/shared/types/entity";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import User from "@mapmycustomers/shared/types/User";
import PlatformFilterModel from "@mapmycustomers/shared/types/viewModel/platformModel/PlatformFilterModel";
import { Button } from "@mapmycustomers/ui";

import DemographicData from "@app/scene/map/types/DemographicData";
import { getUsers } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import AnalyticsContext from "@app/util/contexts/AnalyticsContext";
import { getEntityTypeDisplayName } from "@app/util/ui";
import getDefaultDrawerContainer from "@app/util/ui/getPageContentContainer";

import DataViewMode from "../../enum/DataViewMode";
import { useExtendedAnalytics } from "../../util/analytic/AnalyticsService";
import useAnalytics from "../../util/contexts/useAnalytics";
import DemographyData from "../DemographicData/DemographyData";
import EntityIconTabs from "../EntityIconTabs";

import Data from "./component/Data";
import styles from "./DataView.module.scss";
import messages from "./messages";
import { fetchData, initializeDataView, selectEntityType } from "./store/actions";
import { getDataMode, getEntityType, getField, getSelectedUserIds } from "./store/selectors";
import DataViewEntityTypeCount from "./type/DataViewEntityTypeCount";

interface Props {
  className?: string;
  dataMode: DataViewMode;
  drawerStyle?: React.CSSProperties;
  entityType: EntityTypeSupportingDataView;
  entityTypeRecordsCount: DataViewEntityTypeCount;
  fetchData: typeof fetchData.request;
  field?: IField;
  filter: PlatformFilterModel;
  getDrawerContainer?: () => HTMLElement;
  hideTabs?: boolean;
  hideTotalCount?: boolean;
  initializeDataView: typeof initializeDataView;
  onHide: () => void;
  regionDemographyData?: Omit<DemographicData, "code" | "type">;
  selectedUserIds?: User["id"][];
  selectEntityType: typeof selectEntityType;
  totalCount: number;
  useAnglesCloseButton?: boolean;
  users: User[];
}

const DataView: React.FC<Props> = ({
  className,
  dataMode,
  drawerStyle,
  entityType,
  entityTypeRecordsCount,
  fetchData,
  field,
  filter,
  getDrawerContainer = getDefaultDrawerContainer,
  hideTabs,
  hideTotalCount,
  initializeDataView,
  onHide,
  regionDemographyData,
  selectedUserIds,
  selectEntityType,
  totalCount,
  useAnglesCloseButton,
  users,
}) => {
  const intl = useIntl();

  const analyticIssuer = useExtendedAnalytics("Data View", useAnalytics());

  useEffect(() => {
    analyticIssuer.clicked([]);
    // this effect should be called only once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const analyticIssuerWithEntityType = useExtendedAnalytics(
    getEntityTypeDisplayName(intl, entityType, { lowercase: false, plural: true }),
    analyticIssuer
  );

  useEffect(() => {
    analyticIssuerWithEntityType.clicked([]);
    // this effect should be called only by entity type change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType]);

  const handleHide = useCallback(() => {
    analyticIssuer.clicked(["Close"]);
    onHide();
  }, [analyticIssuer, onHide]);

  useEffect(() => {
    initializeDataView({
      entityTypeRecordsCount,
      filter,
      users,
    });
  }, [entityTypeRecordsCount, filter, initializeDataView, users]);

  useEffect(() => {
    fetchData();
  }, [dataMode, entityType, fetchData, field, filter, selectedUserIds]);

  const isEntityTypeDisabled = useCallback(
    (entityType: EntityTypeSupportingDataView) => entityTypeRecordsCount[entityType] === 0,
    [entityTypeRecordsCount]
  );

  const allEntitiesDisabled =
    isEntityTypeDisabled(EntityType.COMPANY) &&
    isEntityTypeDisabled(EntityType.PERSON) &&
    isEntityTypeDisabled(EntityType.DEAL);
  const getDisabledEntityTypeText = useCallback(
    (entityType: EntityTypeSupportingDataView) => {
      const entityName = getEntityTypeDisplayName(intl, entityType, {
        lowercase: false,
        plural: true,
      });
      return intl.formatMessage(messages.noEntitiesSelected, { entityName });
    },
    [intl]
  );

  return (
    <Drawer
      className={cn(styles.container, className)}
      closable
      closeIcon={
        useAnglesCloseButton ? (
          <Button
            icon={<FontAwesomeIcon icon={faAnglesRight} />}
            iconOnly
            size="small"
            type="text"
          />
        ) : undefined
      }
      contentWrapperStyle={drawerStyle}
      getContainer={getDrawerContainer}
      mask={true}
      onClose={handleHide}
      title={
        <div>
          <span>{intl.formatMessage(messages.title)}</span>
          {!hideTabs && !hideTotalCount && (
            <div className={styles.totalCount}>
              {intl.formatMessage(messages.totalCount, { count: totalCount })}
            </div>
          )}
        </div>
      }
      visible
      width={360}
    >
      <AnalyticsContext.Provider value={analyticIssuerWithEntityType}>
        <div className={styles.bodyContainer}>
          {regionDemographyData && (
            <div>
              <span className={styles.title}>{intl.formatMessage(messages.territory)}</span>
              <DemographyData data={regionDemographyData} theme="light" />
            </div>
          )}
          <div className={styles.recordsContainer}>
            <span className={styles.title}>{intl.formatMessage(messages.records)}</span>
            {!allEntitiesDisabled ? (
              <EntityIconTabs<EntityTypeSupportingDataView>
                activeEntityType={entityType}
                getDisabledText={getDisabledEntityTypeText}
                hideTabs={hideTabs}
                isEntityTypeDisabled={isEntityTypeDisabled}
                onSelect={selectEntityType}
                title={intl.formatMessage(messages.entitiesCount, {
                  count: entityTypeRecordsCount[entityType] ?? 0,
                  entityName: getEntityTypeDisplayName(intl, entityType, {
                    lowercase: false,
                    plural: (entityTypeRecordsCount[entityType] ?? 0) > 1,
                  }),
                })}
              >
                <Data />
              </EntityIconTabs>
            ) : (
              <div className={styles.disabledContainer}>
                <span className={styles.noRecordsTitle}>
                  {intl.formatMessage(messages.noRecords)}
                </span>
                <span>{intl.formatMessage(messages.noRecordDescription)}</span>
              </div>
            )}
          </div>
        </div>
      </AnalyticsContext.Provider>
    </Drawer>
  );
};

const mapStateToProps = (state: RootState) => ({
  dataMode: getDataMode(state),
  entityType: getEntityType(state),
  field: getField(state),
  selectedUserIds: getSelectedUserIds(state),
  users: getUsers(state),
});

const mapDispatchToProps = {
  fetchData: fetchData.request,
  initializeDataView,
  selectEntityType,
};

export default connect(mapStateToProps, mapDispatchToProps)(DataView);
